import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { VALIDATE_AFFILIATE_CODE, TRIGGER_AFFILIATE_CONVERSION } from '@shared/graphql/mutations/affiliate';

interface AffiliateTrackingData {
  affiliateCode: string;
  timestamp: string;
  referrer: string;
  userAgent: string;
  ipAddress?: string;
}

interface UseAffiliateTrackingReturn {
  affiliateCode: string | null;
  isValidCode: boolean | null;
  isLoading: boolean;
  error: string | null;
  validateCode: (code: string) => Promise<boolean>;
  trackConversion: (paymentIntentId: string) => Promise<boolean>;
  clearAffiliate: () => void;
  getAffiliateForRegistration: () => string | undefined;
}

const AFFILIATE_STORAGE_KEY = 'affiliate_tracking';
const AFFILIATE_EXPIRY_DAYS = 30;

// Generate unique tracking ID for this session
const generateTrackingId = (): string => {
  return `track_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

export const useAffiliateTracking = (): UseAffiliateTrackingReturn => {
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [isValidCode, setIsValidCode] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const hasProcessedUrl = useRef(false);

  const [validateAffiliateCode, { loading: validationLoading }] = useLazyQuery(
    VALIDATE_AFFILIATE_CODE,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const isValid = data?.validateAffiliateCode === true;
        setIsValidCode(isValid);
        
        if (!isValid) {
          setError('Invalid affiliate code');
          clearAffiliate();
        } else {
          setError(null);
        }
      },
      onError: (error) => {
        console.error('Affiliate code validation error:', error);
        setError('Error validating affiliate code');
        setIsValidCode(false);
      }
    }
  );

  const [triggerConversion, { loading: conversionLoading }] = useMutation(
    TRIGGER_AFFILIATE_CONVERSION,
    {
      onError: (error) => {
        console.error('Affiliate conversion tracking error:', error);
      }
    }
  );

  const isLoading = validationLoading || conversionLoading;

  // Save affiliate tracking data to localStorage
  const saveAffiliateData = useCallback((code: string) => {
    const trackingData: AffiliateTrackingData = {
      affiliateCode: code,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || 'direct',
      userAgent: navigator.userAgent,
    };

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + AFFILIATE_EXPIRY_DAYS);

    const storageData = {
      ...trackingData,
      expiryDate: expiryDate.toISOString(),
      trackingId: generateTrackingId()
    };

    localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(storageData));
    setAffiliateCode(code);

    console.log(`🔗 Affiliate tracking: Saved code "${code}" with ${AFFILIATE_EXPIRY_DAYS}-day expiry`);
  }, []);

  // Load affiliate tracking data from localStorage
  const loadAffiliateData = useCallback(() => {
    try {
      const storedData = localStorage.getItem(AFFILIATE_STORAGE_KEY);
      if (!storedData) {
        return null;
      }

      const data = JSON.parse(storedData);
      const expiryDate = new Date(data.expiryDate);
      const now = new Date();

      // Check if expired
      if (now > expiryDate) {
        console.log('🔗 Affiliate tracking: Stored code expired, clearing');
        localStorage.removeItem(AFFILIATE_STORAGE_KEY);
        return null;
      }

      console.log(`🔗 Affiliate tracking: Loaded valid code "${data.affiliateCode}"`);
      setAffiliateCode(data.affiliateCode);
      return data.affiliateCode;
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      localStorage.removeItem(AFFILIATE_STORAGE_KEY);
      return null;
    }
  }, []);

  // Clear affiliate tracking data
  const clearAffiliate = useCallback(() => {
    localStorage.removeItem(AFFILIATE_STORAGE_KEY);
    setAffiliateCode(null);
    setIsValidCode(null);
    setError(null);
    console.log('🔗 Affiliate tracking: Cleared stored data');
  }, []);

  // Validate affiliate code
  const validateCode = useCallback(async (code: string): Promise<boolean> => {
    if (!code || code.length < 2) {
      setError('Affiliate code is too short');
      return false;
    }

    try {
      setError(null);
      const result = await validateAffiliateCode({ variables: { code } });
      const isValid = result.data?.validateAffiliateCode === true;
      
      if (isValid) {
        saveAffiliateData(code);
      }
      
      return isValid;
    } catch (error) {
      console.error('Affiliate validation error:', error);
      setError('Error validating affiliate code');
      return false;
    }
  }, [validateAffiliateCode, saveAffiliateData]);

  // Track conversion for payment
  const trackConversion = useCallback(async (paymentIntentId: string): Promise<boolean> => {
    const currentCode = affiliateCode || loadAffiliateData();
    
    if (!currentCode) {
      console.log('🔗 Affiliate tracking: No affiliate code for conversion tracking');
      return false;
    }

    try {
      console.log(`🔗 Affiliate tracking: Triggering conversion for payment ${paymentIntentId} with code ${currentCode}`);
      
      const result = await triggerConversion({
        variables: {
          paymentIntentId,
          affiliateCode: currentCode
        }
      });

      const success = result.data?.triggerAffiliateConversion?.success === true;
      
      if (success) {
        console.log('🔗 Affiliate tracking: Conversion tracked successfully');
      } else {
        console.warn('🔗 Affiliate tracking: Conversion tracking failed:', result.data?.triggerAffiliateConversion?.message);
      }

      return success;
    } catch (error) {
      console.error('Affiliate conversion tracking error:', error);
      return false;
    }
  }, [affiliateCode, triggerConversion, loadAffiliateData]);

  // Get affiliate code for registration (without validation)
  const getAffiliateForRegistration = useCallback((): string | undefined => {
    return affiliateCode || loadAffiliateData() || undefined;
  }, [affiliateCode, loadAffiliateData]);

  // Process URL parameters on mount and route changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refParam = searchParams.get('ref');

    if (refParam && !hasProcessedUrl.current) {
      hasProcessedUrl.current = true;
      console.log(`🔗 Affiliate tracking: Found ref parameter: ${refParam}`);
      
      // Validate and save the affiliate code
      validateCode(refParam).then(isValid => {
        if (isValid) {
          console.log(`🔗 Affiliate tracking: Successfully validated and saved code: ${refParam}`);
        } else {
          console.warn(`🔗 Affiliate tracking: Invalid affiliate code: ${refParam}`);
        }
      });
    } else if (!refParam && !hasProcessedUrl.current) {
      // No ref parameter, but check if we have stored data
      hasProcessedUrl.current = true;
      loadAffiliateData();
    }
  }, [location.search, validateCode, loadAffiliateData]);

  // Load existing affiliate data on mount
  useEffect(() => {
    if (!affiliateCode) {
      loadAffiliateData();
    }
  }, [affiliateCode, loadAffiliateData]);

  return {
    affiliateCode,
    isValidCode,
    isLoading,
    error,
    validateCode,
    trackConversion,
    clearAffiliate,
    getAffiliateForRegistration
  };
};

export default useAffiliateTracking;